import { z } from 'zod'

import {
  brandList,
  Brand,
} from '@awaze/design-system.components.awaze-theme-provider'

const validLocales = [
  'en',
  'hr',
  'hr-HR',
  'da',
  'da-DK',
  'nl',
  'nl-BE',
  'nl-NL',
  'en-EU',
  'en-GB',
  'en-US',
  'fr',
  'fr-FR',
  'de',
  'de-AT',
  'de-DE',
  'de-CH',
  'it',
  'it-IT',
  'nb',
  'nb-NO',
  'pl',
  'pl-PL',
  'es',
  'es-ES',
  'sv',
  'sv-SE',
] as const

type ValidLocale = (typeof validLocales)[number]

const TimeLabelText = z
  .object({
    days: z.string().min(1),
    hours: z.string().min(1),
    minutes: z.string().min(1),
    seconds: z.string().min(1),
  })
  .strict()

const Campaign = z
  .object({
    primaryTextDefault: z.string().min(1),
    primaryTextMobile: z.string().min(1),
    secondaryTextDefault: z.string().min(1),
    secondaryTextMobile: z.string().min(1),
    variant: z.enum(['primary', 'secondary']),
    endDate: z.iso.datetime({ offset: true }),
    timeLabelText: TimeLabelText,
  })
  .strict()

const LocaleMap = z
  .record(z.string().min(1), Campaign)
  .superRefine((locales, ctx) => {
    const keys = Object.keys(locales)

    if (Object.keys(locales).length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Each included brand must have at least one locale',
      })
    }

    for (const locale of keys) {
      if (!validLocales.includes(locale as ValidLocale)) {
        ctx.addIssue({
          path: [locale],
          code: 'custom',
          message: `Unknown locale '${locale}'. Must be one of: ${validLocales.join(', ')}`,
        })
      }
    }
  })

const CampaignCopySchema = z.object({
  banner_text: z
    .record(z.string().min(1), LocaleMap)
    .superRefine((brands, ctx) => {
      const keys = Object.keys(brands)

      if (keys.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'At least one brand is required',
        })
      }

      for (const brand of keys) {
        if (!brandList.includes(brand as Brand)) {
          ctx.addIssue({
            path: [brand],
            code: 'custom',
            message: `Unknown brand '${brand}'. Must be one of: ${brandList.join(', ')}`,
          })
        }
      }
    }),
})

export type CampaignCopySchemaOutput = z.output<typeof CampaignCopySchema>

export const CampaignCopySchemaRequiring = (
  requiredBrand: Brand,
  requiredLocale: string,
) =>
  CampaignCopySchema.superRefine((obj, ctx) => {
    if (!obj.banner_text[requiredBrand]) {
      ctx.addIssue({
        code: 'custom',
        path: ['banner_text'],
        message: `Required brand "${requiredBrand}" is missing`,
      })

      return
    }

    if (!obj.banner_text[requiredBrand][requiredLocale]) {
      ctx.addIssue({
        code: 'custom',
        path: ['banner_text', requiredBrand],
        message: `Required locale "${requiredLocale}" is missing for brand "${requiredBrand}"`,
      })
    }
  })
